/**
 * Client-side rate limiting utility
 * Protects against excessive API requests and potential DoS
 */

interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  blockDurationMs: number;
}

interface RequestRecord {
  count: number;
  windowStart: number;
  blocked: boolean;
  blockEnd?: number;
}

class RateLimiter {
  private records: Map<string, RequestRecord> = new Map();
  private configs: Map<string, RateLimitConfig> = new Map();

  constructor() {
    // Default configurations for different endpoint types
    this.setLimits('auth', { maxRequests: 20, windowMs: 5 * 60 * 1000, blockDurationMs: 2 * 60 * 1000 }); // 20 requests per 5 minutes, block for 2 minutes
    this.setLimits('api', { maxRequests: 500, windowMs: 60 * 1000, blockDurationMs: 15 * 1000 }); // 500 requests per minute, block for 15 seconds
    this.setLimits('upload', { maxRequests: 20, windowMs: 60 * 1000, blockDurationMs: 5 * 60 * 1000 }); // 20 uploads per minute, block for 5 minutes
  }

  /**
   * Set rate limit configuration for a specific endpoint type
   */
  setLimits(endpointType: string, config: RateLimitConfig): void {
    this.configs.set(endpointType, config);
  }

  /**
   * Check if request is allowed and update counters
   */
  checkRequest(endpoint: string, endpointType: string = 'api'): { allowed: boolean; retryAfter?: number; message?: string } {
    const config = this.configs.get(endpointType);
    if (!config) {
      return { allowed: true };
    }

    const now = Date.now();
    const key = `${endpointType}:${endpoint}`;
    let record = this.records.get(key);

    // Initialize record if it doesn't exist
    if (!record) {
      record = { count: 0, windowStart: now, blocked: false };
      this.records.set(key, record);
    }

    // Check if currently blocked
    if (record.blocked && record.blockEnd && now < record.blockEnd) {
      const retryAfter = Math.ceil((record.blockEnd - now) / 1000);
      return {
        allowed: false,
        retryAfter,
        message: `Rate limit exceeded. Try again in ${retryAfter} seconds.`
      };
    }

    // Reset window if expired
    if (now - record.windowStart >= config.windowMs) {
      record.count = 0;
      record.windowStart = now;
      record.blocked = false;
      record.blockEnd = undefined;
    }

    // Check if limit exceeded
    if (record.count >= config.maxRequests) {
      record.blocked = true;
      record.blockEnd = now + config.blockDurationMs;
      const retryAfter = Math.ceil(config.blockDurationMs / 1000);
      
      return {
        allowed: false,
        retryAfter,
        message: `Rate limit exceeded. Blocked for ${Math.ceil(config.blockDurationMs / 60000)} minutes.`
      };
    }

    // Increment counter and allow request
    record.count++;
    return { allowed: true };
  }

  /**
   * Get rate limit status for debugging
   */
  getStatus(endpoint: string, endpointType: string = 'api'): RequestRecord | null {
    const key = `${endpointType}:${endpoint}`;
    return this.records.get(key) || null;
  }

  /**
   * Reset rate limits for an endpoint (admin function)
   */
  reset(endpoint: string, endpointType: string = 'api'): void {
    const key = `${endpointType}:${endpoint}`;
    this.records.delete(key);
  }

  /**
   * Clean up expired records to prevent memory leaks
   */
  cleanup(): void {
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours

    const keysToDelete: string[] = [];
    this.records.forEach((record, key) => {
      if (now - record.windowStart > maxAge) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.records.delete(key));
  }
}

// Singleton instance
export const rateLimiter = new RateLimiter();

// Auto cleanup every hour
setInterval(() => {
  rateLimiter.cleanup();
}, 60 * 60 * 1000);

export default rateLimiter;